import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bool, func, oneOf } from 'prop-types';
import _ from 'lodash';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
    Close as CloseIcon,
    PlayArrow as PlayArrowIcon,
    NavigateBefore as NavigateBeforeIcon,
    Visibility as VisibilityIcon,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';

import { useTimezone, useUser } from '../../../stores/SessionStore';
import useStyles from './ReportDialogStyles';
import { useReportService, buildReportOptions, isNewReport } from '../../../services/ReportService';
import ReportOptions, { optionsType, defaultOptions, MODES, MODE_LABELS } from './ReportOptions';
import ReportPreview from './ReportPreview';

export const REPORT_MODES = MODES;
export const REPORT_MODE_LABELS = MODE_LABELS;

export default function ReportDialog({ open, reportMode, reportOptions, onCancel, onOk, onRun }) {
    const [user] = useUser();
    const [timezone] = useTimezone();
    const [options, setOptions] = useState(defaultOptions);
    const [showPreview, setShowPreview] = useState(false);
    const [optionsValidity, setOptionsValidity] = useState({
        all: false,
        pdf: false,
    });
    const classes = useStyles({
        showPreview,
    });
    const modeLabel = useMemo(() => i18n._(REPORT_MODE_LABELS[reportMode]), [reportMode]);

    useEffect(() => {
        setOptions(
            _.merge({}, defaultOptions, reportOptions, {
                user: reportOptions?.user || user.id,
                recurrence: {
                    timezone: reportOptions?.recurrence?.timezone || timezone,
                },
            })
        );
        // This both depends on and changes options.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, timezone, reportOptions]);

    function reset() {
        setShowPreview(false);
        setOptions(
            _.merge({}, defaultOptions, reportOptions, {
                user: user.id,
                recurrence: {
                    timezone,
                },
            })
        );
    }

    function handleCancel() {
        onCancel();
        reset();
    }

    function handleOk() {
        onOk(options);
        reset();
    }

    function handleRun() {
        onRun(options);
    }

    return (
        <Dialog id="report-dialog" className={classes.root} maxWidth="md" open={open} onClose={onCancel}>
            <DialogTitle>
                {modeLabel} <Trans>PDF Report</Trans>
                <IconButton aria-label="cancel" className={classes.closeButton} onClick={handleCancel}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {showPreview ? (
                    <ReportPreview options={options} />
                ) : (
                    <ReportOptions
                        mode={reportMode}
                        options={options}
                        onChange={setOptions}
                        onValidate={setOptionsValidity}
                    />
                )}
            </DialogContent>
            <DialogActions id="report-dialog-actions">
                <div className={classes.leftActions}>
                    {showPreview ? (
                        <Button
                            id="report-dialog-action-back"
                            startIcon={<NavigateBeforeIcon />}
                            onClick={() => setShowPreview(false)}
                        >
                            <Trans>Back</Trans>
                        </Button>
                    ) : (
                        <>
                            {reportMode === MODES.SCHEDULE && (
                                <Button
                                    id="report-dialog-action-run"
                                    disabled={!optionsValidity.all}
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleRun}
                                >
                                    <Trans>Run Report Now</Trans>
                                </Button>
                            )}
                            <Button
                                id="report-dialog-action-preview"
                                disabled={!optionsValidity.pdf}
                                startIcon={<VisibilityIcon />}
                                onClick={() => setShowPreview(true)}
                            >
                                <Trans>Preview Report</Trans>
                            </Button>
                        </>
                    )}
                </div>
                <Button id="report-dialog-action-cancel" onClick={handleCancel}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button
                    color="primary"
                    id="report-dialog-action-save"
                    disabled={!optionsValidity.all}
                    onClick={handleOk}
                >
                    {isNewReport(reportOptions) ? modeLabel : <Trans>Save</Trans>}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ReportDialog.propTypes = {
    open: bool.isRequired,
    reportMode: oneOf(Object.values(MODES)).isRequired,
    reportOptions: optionsType.isRequired,
    onCancel: func.isRequired,
    onOk: func.isRequired,
    onRun: func.isRequired,
};

export function useReportDialog() {
    const reportService = useReportService();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState(MODES.DOWNLOAD);
    const [options, setOptions] = useState(defaultOptions);
    const { enqueueSnackbar } = useSnackbar();
    const onCompleteRef = useRef();
    const onCancel = useCallback(() => {
        setOpen(false);
    }, []);
    const onOk = useCallback(
        (newOptions) => {
            const reportOptions = buildReportOptions({
                ...options,
                ...newOptions,
            });

            setOpen(false);

            switch (mode) {
                case REPORT_MODES.EMAIL:
                    reportService.emailPdf(reportOptions).then(() => {
                        enqueueSnackbar(i18n._(t`Your report is being generated and will be sent when it is complete`));
                        if (_.isFunction(onCompleteRef.current)) onCompleteRef.current();
                    });
                    break;

                case REPORT_MODES.SCHEDULE:
                    reportService.schedulePdf(reportOptions).then(() => {
                        if (isNewReport(reportOptions)) {
                            enqueueSnackbar(i18n._(t`Your report has been scheduled`));
                        } else {
                            enqueueSnackbar(i18n._(t`Your report has been saved`));
                        }
                        if (_.isFunction(onCompleteRef.current)) onCompleteRef.current();
                    });
                    break;

                case REPORT_MODES.DOWNLOAD:
                default:
                    reportService.downloadPdf(reportOptions).then(() => {
                        if (_.isFunction(onCompleteRef.current)) onCompleteRef.current();
                    });
                    enqueueSnackbar(i18n._(t`Your report is being generated and will download when it is complete`));
                    break;
            }
        },
        [reportService, mode, options, enqueueSnackbar]
    );
    const onRun = useCallback(
        (runOptions) => {
            const reportOptions = buildReportOptions(runOptions);
            reportService.emailPdf(reportOptions);
            enqueueSnackbar(i18n._(t`Your report is being generated and will be sent when it is complete`));
        },
        [reportService, enqueueSnackbar]
    );
    const showDialog = useCallback((reportMode, completeCallback, reportOptions) => {
        onCompleteRef.current = completeCallback;
        setMode(reportMode);
        setOptions(reportOptions);
        setOpen(true);
    }, []);

    const dialog = (
        <ReportDialog
            open={open}
            reportMode={mode}
            reportOptions={options}
            onCancel={onCancel}
            onOk={onOk}
            onRun={onRun}
        />
    );

    return [dialog, showDialog];
}
